<template>
    <div class="analytics-results-findings-view rounded-xl border border-stone-200 bg-[#faf8f5] shadow-sm overflow-hidden">
        <div v-if="fromAttention" class="flex items-center justify-between gap-3 border-b border-stone-200 bg-white/80 px-5 py-3">
            <p class="text-sm text-stone-600">Supporting evidence for this point</p>
            <a
                v-if="attentionDashboardUrl"
                :href="attentionDashboardUrl"
                class="shrink-0 text-sm font-medium text-stone-700 underline hover:text-stone-900"
            >
                &larr; Back to Attention Dashboard
            </a>
        </div>

        <div class="px-5 pt-5 pb-4">
            <p class="text-sm font-medium text-stone-700">{{ dateRangeLabel }}</p>

            <div v-if="isLoading" class="mt-4 text-center py-16 text-sm text-stone-500">
                <svg class="animate-spin inline-block -ml-1 mr-2 h-5 w-5 text-stone-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading sensor readings…
            </div>

            <div v-else-if="errorMessage" class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800">
                {{ errorMessage }}
                <button type="button" class="ml-2 underline font-medium" @click="loadData">Try again</button>
            </div>

            <template v-else>
                <div class="mt-3 relative">
                    <div class="relative h-9 rounded-md bg-[#6b7c3e] shadow-inner overflow-hidden">
                        <div
                            v-for="(band, idx) in timelineBands"
                            :key="`tl-${idx}`"
                            class="absolute top-1 bottom-1 rounded-sm bg-[#8fa654]/90 border border-white/30"
                            :style="timelineBandStyle(band)"
                        ></div>
                        <div class="absolute inset-y-0 left-0 w-2 flex items-center justify-center">
                            <span class="h-5 w-1.5 rounded-full bg-white/90 shadow"></span>
                        </div>
                        <div class="absolute inset-y-0 right-0 w-2 flex items-center justify-center">
                            <span class="h-5 w-1.5 rounded-full bg-white/90 shadow"></span>
                        </div>
                        <div class="absolute inset-0 flex items-center justify-between px-6 pointer-events-none">
                            <span
                                v-for="(tick, idx) in timelineTicks"
                                :key="`tick-${idx}`"
                                class="text-[10px] font-medium text-white/90"
                            >
                                {{ tick }}
                            </span>
                        </div>
                    </div>

                    <div class="relative mt-2 rounded-lg border border-stone-200 bg-white">
                        <div ref="chartContainer" :style="graphHeight" class="w-full"></div>
                    </div>

                    <div class="mt-2 flex flex-wrap gap-4 text-xs text-stone-600 px-1">
                        <span class="inline-flex items-center gap-1.5">
                            <span class="inline-block h-0.5 w-5 rounded-full bg-[#3d81f7]"></span>
                            Sensor readings
                        </span>
                        <span v-if="overlayData" class="inline-flex items-center gap-1.5">
                            <span class="inline-block h-0.5 w-5 rounded-full bg-amber-500" style="border-top: 2px dashed #d97706; background: transparent;"></span>
                            30-day trend
                        </span>
                        <span v-if="overlayData && overlayData.change_points && overlayData.change_points.length" class="inline-flex items-center gap-1.5">
                            <span class="inline-block h-3.5 w-px border-l-2 border-dashed border-amber-800"></span>
                            Shift detected
                        </span>
                        <span v-if="overlayData && overlayData.change_points && overlayData.change_points.length" class="inline-flex items-center gap-1.5">
                            <span class="inline-block h-0.5 w-5" style="border-top: 1.5px dashed #64748b;"></span>
                            Avg before / after
                        </span>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="!isLoading && !errorMessage" class="border-t border-stone-200 bg-white/60 px-5 py-5">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
                <div class="flex flex-wrap items-center gap-2">
                    <h2 class="text-sm font-bold uppercase tracking-wide text-red-700">
                        What we're seeing ({{ sortedFindings.length }})
                    </h2>
                    <span
                        v-if="topSeverityMeta"
                        class="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                        :class="topSeverityMeta.pillClass"
                    >
                        {{ topSeverityMeta.label }}
                    </span>
                </div>
                <p v-if="summaryLine" class="text-sm text-stone-600 sm:text-right max-w-md">{{ summaryLine }}</p>
            </div>

            <div v-if="!sortedFindings.length" class="rounded-lg border border-stone-200 bg-white px-4 py-8 text-center text-sm text-stone-500">
                No analytical findings for this point right now.
            </div>

            <div v-else class="divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white overflow-hidden">
                <div
                    v-for="(finding, idx) in sortedFindings"
                    :key="findingKey(finding, idx)"
                    class="grid grid-cols-1 lg:grid-cols-12 gap-3 px-4 py-4 transition-colors cursor-pointer"
                    :class="rowClasses(finding)"
                    @click="focusFinding(finding)"
                >
                    <div class="lg:col-span-5 min-w-0">
                        <div class="flex items-start gap-3">
                            <span
                                class="shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold mt-0.5"
                                :class="severityMeta(finding.severity).pillClass"
                            >
                                {{ severityMeta(finding.severity).label }}
                            </span>
                            <div class="min-w-0">
                                <p class="font-semibold text-stone-900 leading-snug">{{ finding.label }}</p>
                                <p v-if="finding.detail" class="mt-0.5 text-sm text-stone-600 leading-relaxed">{{ finding.detail }}</p>
                                <p v-if="supportingText(finding)" class="mt-1 text-xs font-medium text-stone-700">
                                    {{ supportingText(finding) }}
                                </p>
                                <p
                                    v-if="finding.supporting && finding.supporting.latest_description"
                                    data-insights-detail
                                    class="mt-0.5 text-[11px] text-stone-500"
                                >
                                    {{ finding.supporting.latest_description }}
                                </p>
                                <p class="mt-1 text-xs text-stone-500">
                                    Noticed {{ relativeTime(finding.detected_at) }}
                                    <span v-if="finding.is_stale"> · older finding</span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="lg:col-span-5 text-sm text-stone-600 italic leading-relaxed">
                        <span v-if="finding.why">Why flagged: {{ finding.why }}</span>
                    </div>
                    <div class="lg:col-span-2 flex items-start lg:justify-end gap-2 text-sm text-stone-700">
                        <span
                            class="shrink-0 mt-1.5 h-2 w-2 rounded-full"
                            :style="{ backgroundColor: severityMeta(finding.severity).dotColor }"
                        ></span>
                        <span class="font-medium whitespace-nowrap">{{ findingDateRangeLabel(finding) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin';
import { amchartsMixin } from '@/mixins/amchartsMixin';
import { maxChartPointsForSpan, simplifyChartPoints, simplifyChartPointsForRange } from '@/utils/simplifyChartPoints';
import {
    ANALYTICS_LOOKBACK_DAYS as LOOKBACK_DAYS,
    CHART_MAX_POINTS,
    DRIFT_METRICS,
    DRIFT_TYPES,
    METER_LINE_COLOR,
    estimateSwing,
    fetchMeterReadings,
} from '@/utils/analyticsFindingsShared';

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const MOVING_AVG_COLOR = '#d97706';   // amber-600

const SEVERITY_META = {
    high: {
        label: 'High',
        pillClass: 'bg-rose-100 text-rose-800 border-rose-300',
        dotColor: '#e11d48',
        bandFill: '#fecaca',
        bandOpacity: 0.45,
        pillBorder: '#f87171',
        pillBg: '#fff1f2',
    },
    medium: {
        label: 'Medium',
        pillClass: 'bg-amber-50 text-amber-900 border-amber-300',
        dotColor: '#d97706',
        bandFill: '#fde68a',
        bandOpacity: 0.42,
        pillBorder: '#fbbf24',
        pillBg: '#fffbeb',
    },
    low: {
        label: 'Low',
        pillClass: 'bg-yellow-50 text-yellow-900 border-yellow-200',
        dotColor: '#ca8a04',
        bandFill: '#fef9c3',
        bandOpacity: 0.38,
        pillBorder: '#fde047',
        pillBg: '#fefce8',
    },
};

export default {
    name: 'AnalyticsResultsFindingsView',
    mixins: [SonicuCoreMixin, amchartsMixin],
    props: {
        meterId: {
            type: Number,
            required: true,
        },
        findings: {
            type: Array,
            default: () => [],
        },
        focusedMetricKey: {
            type: String,
            default: '',
        },
        fromAttention: {
            type: Boolean,
            default: false,
        },
        attentionDashboardUrl: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            units: '',
            points: [],
            displayPoints: [],
            dataStartMs: null,
            dataEndMs: null,
            graphBands: [],
            timelineBands: [],
            activeFocusKey: '',
            isZoomedToFinding: false,
            suppressRangeUpdate: false,
            overlayData: null,
            _chart: null,
            _xAxis: null,
            _yAxis: null,
            _series: null,
            _axisRanges: [],
            _yAxisRanges: [],
            _htmlLabels: [],
        };
    },
    computed: {
        graphHeight() {
            const clientHeight = Math.max(document.documentElement.clientHeight - 520, 380);
            return { height: `${clientHeight}px`, width: '100%' };
        },
        sortedFindings() {
            return [...(this.findings || [])].sort((a, b) => {
                const sev = this.severityWeight(b.severity) - this.severityWeight(a.severity);
                if (sev !== 0) {
                    return sev;
                }
                return String(b.detected_at || '').localeCompare(String(a.detected_at || ''));
            });
        },
        topSeverityMeta() {
            const top = this.sortedFindings[0];
            if (!top) {
                return null;
            }
            return this.severityMeta(top.severity);
        },
        summaryLine() {
            if (!this.sortedFindings.length) {
                return '';
            }
            const top = this.sortedFindings[0];
            const more = this.sortedFindings.length - 1;
            if (more <= 0) {
                return top.label;
            }
            return `${top.label} + ${more} more`;
        },
        dateRangeLabel() {
            if (!this.dataStartMs || !this.dataEndMs) {
                const end = new Date();
                const start = new Date();
                start.setDate(start.getDate() - LOOKBACK_DAYS);
                return `${this.formatShortDate(start)} – ${this.formatShortDate(end)}`;
            }
            return `${this.formatShortDate(this.dataStartMs)} – ${this.formatShortDate(this.dataEndMs)}`;
        },
        timelineTicks() {
            if (!this.dataStartMs || !this.dataEndMs) {
                return [];
            }
            const span = this.dataEndMs - this.dataStartMs;
            const count = 5;
            const ticks = [];
            for (let i = 0; i < count; i += 1) {
                const ms = this.dataStartMs + (span * (i / (count - 1)));
                ticks.push(this.formatTickDate(ms));
            }
            return ticks;
        },
    },
    created() {
        Object.defineProperty(this, 'root', {
            value: null,
            writable: true,
            enumerable: false,
            configurable: true,
        });
        this.initializeAM5Charts();
        this.activeFocusKey = this.focusedMetricKey || '';
    },
    mounted() {
        this.$nextTick(() => {
            this.loadData();
        });
    },
    beforeUnmount() {
        this.disposeChart();
    },
    methods: {
        async loadData() {
            this.isLoading = true;
            this.errorMessage = '';

            try {
                const { points, units } = await fetchMeterReadings(this.company, this.meterId, LOOKBACK_DAYS);
                this.points = points;

                if (!this.points.length) {
                    this.errorMessage = 'No sensor readings are available for the last 90 days.';
                    this.isLoading = false;
                    return;
                }

                this.units = units;
                this.dataStartMs = this.points[0].date;
                this.dataEndMs = this.points[this.points.length - 1].date;
                this.displayPoints = simplifyChartPoints(this.points, CHART_MAX_POINTS);
                this.graphBands = this.buildGraphBands();
                this.timelineBands = this.graphBands;

                // Fetch trend overlay data (moving average + change points) in parallel
                try {
                    const overlayResp = await axios.get(
                        `${this.company.buildCompanyApiInternalRoute('analytics-meter')}/${this.meterId}`,
                        { params: { metric: 'hybrid_trend_shift' } },
                    );
                    if (overlayResp.data?.success) {
                        this.overlayData = overlayResp.data;
                    }
                } catch {
                    // Overlay is best-effort; raw readings still show
                    this.overlayData = null;
                }

                this.isLoading = false;
                this.$nextTick(() => {
                    this.renderChart();
                    if (this.activeFocusKey) {
                        const finding = this.sortedFindings.find((f) => f.metric_key === this.activeFocusKey);
                        if (finding) {
                            this.focusFinding(finding, false);
                        }
                    }
                });
            } catch (error) {
                this.errorMessage = error.response?.data?.message || error.message || 'Unable to load chart.';
                this.isLoading = false;
            }
        },

        buildGraphBands() {
            const findings = this.sortedFindings;
            if (!findings.length) {
                return [];
            }

            const bands = [];
            const driftFindings = [];
            const otherFindings = [];

            findings.forEach((finding) => {
                if (this.isDriftFinding(finding)) {
                    driftFindings.push(finding);
                } else if (finding.type !== 'peer_outlier' && finding.metric_key !== 'category_outlier') {
                    otherFindings.push(finding);
                }
            });

            driftFindings.forEach((finding) => {
                const window = this.findingWindow(finding);
                bands.push({
                    startMs: window.startMs,
                    endMs: window.endMs,
                    label: this.graphBandLabel(finding),
                    severity: finding.severity,
                    metricKey: finding.metric_key,
                    finding,
                });
            });

            const recentCutoff = this.dataEndMs - (30 * 86400000);
            const recentOthers = otherFindings.filter((finding) => {
                const detected = this.detectedMs(finding);
                return detected == null || detected >= recentCutoff;
            });

            if (recentOthers.length > 1) {
                const windows = recentOthers.map((f) => this.findingWindow(f));
                const startMs = Math.min(...windows.map((w) => w.startMs));
                const topSeverity = recentOthers.reduce(
                    (best, f) => (this.severityWeight(f.severity) > this.severityWeight(best) ? f.severity : best),
                    'low',
                );
                bands.push({
                    startMs,
                    endMs: this.dataEndMs,
                    label: `${recentOthers.length} recent findings`,
                    severity: topSeverity,
                    metricKey: '__recent_group__',
                    finding: recentOthers[0],
                    grouped: true,
                });
            } else {
                recentOthers.forEach((finding) => {
                    const window = this.findingWindow(finding);
                    bands.push({
                        startMs: window.startMs,
                        endMs: window.endMs,
                        label: this.graphBandLabel(finding),
                        severity: finding.severity,
                        metricKey: finding.metric_key,
                        finding,
                    });
                });
            }

            const olderOthers = otherFindings.filter((finding) => !recentOthers.includes(finding));
            olderOthers.forEach((finding) => {
                const window = this.findingWindow(finding);
                bands.push({
                    startMs: window.startMs,
                    endMs: window.endMs,
                    label: this.graphBandLabel(finding),
                    severity: finding.severity,
                    metricKey: finding.metric_key,
                    finding,
                });
            });

            return bands;
        },

        renderChart() {
            this.disposeChart();

            const container = this.$refs.chartContainer;
            if (!container) {
                return;
            }

            this.root = am5.Root.new(container);
            this.root.setThemes([am5themes_Animated.new(this.root)]);

            const chart = this.root.container.children.push(
                am5xy.XYChart.new(this.root, {
                    panX: true,
                    panY: false,
                    wheelX: 'panX',
                    wheelY: 'zoomX',
                    pinchZoomX: true,
                    paddingTop: 12,
                    paddingRight: 12,
                    paddingBottom: 12,
                    paddingLeft: 8,
                }),
            );

            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(this.root, {
                    baseInterval: { timeUnit: 'second', count: 1 },
                    renderer: am5xy.AxisRendererX.new(this.root, {
                        minGridDistance: 60,
                    }),
                    tooltip: am5.Tooltip.new(this.root, {}),
                }),
            );
            this._xAxis = xAxis;

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(this.root, {
                    renderer: am5xy.AxisRendererY.new(this.root, {
                        minGridDistance: 30,
                    }),
                }),
            );
            this._yAxis = yAxis;

            const yLabel = am5.Label.new(this.root, {
                rotation: -90,
                text: this.units ? `(${this.units})` : '',
                y: am5.p50,
                centerX: am5.p50,
                fontSize: 12,
                fill: am5.color('#57534e'),
            });
            yAxis.children.unshift(yLabel);

            yAxis.get('renderer').labels.template.adapters.add('text', (text) => {
                if (text === undefined || text === null || !this.units) {
                    return text;
                }
                return `${text}`;
            });

            const series = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: 'Readings',
                    xAxis,
                    yAxis,
                    valueYField: 'value',
                    valueXField: 'date',
                    connect: true,
                    tensionX: 1,
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: `{valueY.formatNumber('#.##')}${this.units ? ` ${this.units}` : ''}\n{valueX.formatDate('MMM d, yyyy h:mm a')}`,
                    }),
                }),
            );
            series.set('stroke', am5.color(METER_LINE_COLOR));
            series.strokes.template.setAll({
                stroke: am5.color(METER_LINE_COLOR),
                strokeWidth: 1.5,
            });
            series.fills.template.setAll({ visible: false });
            series.data.setAll(this.displayPoints);
            this._series = series;

            this.graphBands.forEach((band) => {
                this.addFindingBand(xAxis, band);
            });

            this.renderOverlays(chart, xAxis, yAxis);

            xAxis.events.on('rangechangeended', () => {
                if (this.suppressRangeUpdate) {
                    return;
                }
                this.updateSeriesForVisibleRange();
            });

            chart.set('cursor', am5xy.XYCursor.new(this.root, {
                behavior: 'zoomX',
                snapToSeriesBy: 'x',
                xAxis,
            }));

            this._chart = chart;
        },

        updateSeriesForVisibleRange() {
            if (!this._series || !this._xAxis || !this.points.length) {
                return;
            }

            const startMs = this._xAxis.getPrivate('selectionMin');
            const endMs = this._xAxis.getPrivate('selectionMax');
            this.applySeriesDataForRange(startMs, endMs);
        },

        applySeriesDataForRange(startMs, endMs) {
            if (!this._series || !this.points.length) {
                return;
            }

            const rangeStart = startMs ?? this.dataStartMs;
            const rangeEnd = endMs ?? this.dataEndMs;
            const fullSpan = this.dataEndMs - this.dataStartMs;
            const visibleSpan = rangeEnd - rangeStart;
            const maxPoints = maxChartPointsForSpan(visibleSpan, fullSpan, CHART_MAX_POINTS);
            const simplified = simplifyChartPointsForRange(
                this.points,
                rangeStart,
                rangeEnd,
                maxPoints,
            );

            this._series.data.clear();
            this._series.data.setAll(simplified);

            const yAxis = this._chart?.yAxes?.getIndex(0);
            if (yAxis) {
                yAxis.set('min', undefined);
                yAxis.set('max', undefined);
            }
        },

        renderOverlays(chart, xAxis, yAxis) {
            if (!this.overlayData) {
                return;
            }

            const maPoints = (this.overlayData.data || [])
                .map((row) => {
                    const ms = new Date(row.full_date + 'T12:00:00').getTime();
                    const val = Number(row.moving_average);
                    return Number.isNaN(ms) || Number.isNaN(val) ? null : { date: ms, value: val };
                })
                .filter(Boolean);

            if (maPoints.length) {
                const maTooltip = am5.Tooltip.new(this.root, {
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    autoTextColor: false,
                    labelText: [
                        '[bold]30-day rolling average[/]',
                        '{valueY.formatNumber(\'#.##\')}' + (this.units ? ` ${this.units}` : ''),
                        '[#92400e fontSize: 10px]Smoothed trend — averages each day\'s readings\nover the surrounding 30-day window to show\ndrift direction without short-term noise.[/]',
                    ].join('\n'),
                });

                maTooltip.get('background').setAll({
                    fill: am5.color('#fffbeb'),
                    fillOpacity: 0.97,
                    stroke: am5.color('#f59e0b'),
                    strokeWidth: 1,
                    cornerRadiusTL: 6,
                    cornerRadiusTR: 6,
                    cornerRadiusBL: 6,
                    cornerRadiusBR: 6,
                });
                maTooltip.label.setAll({
                    fill: am5.color('#78350f'),
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingLeft: 10,
                    paddingRight: 10,
                    fontSize: 12,
                    lineHeight: 1.5,
                });

                const maSeries = chart.series.push(
                    am5xy.LineSeries.new(this.root, {
                        name: '30-day trend',
                        xAxis,
                        yAxis,
                        valueYField: 'value',
                        valueXField: 'date',
                        connect: true,
                        tensionX: 1,
                        tooltip: maTooltip,
                    }),
                );
                maSeries.set('stroke', am5.color(MOVING_AVG_COLOR));
                maSeries.strokes.template.setAll({
                    stroke: am5.color(MOVING_AVG_COLOR),
                    strokeWidth: 2,
                    strokeDasharray: [6, 4],
                });
                maSeries.fills.template.setAll({ visible: false });
                maSeries.data.setAll(maPoints);
            }

            const changePoints = this.overlayData.change_points || [];
            changePoints.forEach((cp) => {
                const ts = new Date(cp.timestamp).getTime();
                if (!Number.isFinite(ts)) {
                    return;
                }

                const gridRange = xAxis.createAxisRange(xAxis.makeDataItem({ value: ts }));
                gridRange.get('grid').setAll({
                    stroke: am5.color('#92400e'),
                    strokeWidth: 2,
                    strokeOpacity: 0.8,
                    strokeDasharray: [4, 3],
                });
                gridRange.get('label').setAll({ visible: false });
                this._axisRanges.push(gridRange);

                const direction = cp.direction;
                if (direction === 'upward' || direction === 'downward') {
                    const arrowLabel = chart.plotContainer.children.push(
                        am5.Label.new(this.root, {
                            text: direction === 'upward' ? '↑ Shift up' : '↓ Shift down',
                            fontSize: 11,
                            fontWeight: '600',
                            fill: am5.color('#92400e'),
                            background: am5.RoundedRectangle.new(this.root, {
                                fill: am5.color('#fef3c7'),
                                fillOpacity: 0.92,
                                stroke: am5.color('#f59e0b'),
                                strokeWidth: 1,
                                cornerRadiusTL: 4,
                                cornerRadiusTR: 4,
                                cornerRadiusBL: 4,
                                cornerRadiusBR: 4,
                            }),
                            paddingTop: 3,
                            paddingBottom: 3,
                            paddingLeft: 7,
                            paddingRight: 7,
                        }),
                    );

                    // Position label at the change-point x, top of plot
                    const positionLabel = () => {
                        const xPos = xAxis.valueToPosition(ts);
                        const plotW = chart.plotContainer.width();
                        if (Number.isFinite(xPos) && Number.isFinite(plotW)) {
                            arrowLabel.set('x', xPos * plotW);
                            arrowLabel.set('y', 8);
                            arrowLabel.set('centerX', am5.p50);
                        }
                    };
                    positionLabel();
                    xAxis.onPrivate('selectionMin', positionLabel);
                    xAxis.onPrivate('selectionMax', positionLabel);
                    this._htmlLabels.push(arrowLabel);
                }

                // Horizontal mean lines
                if (cp.mean_before != null) {
                    const beforeRange = yAxis.createAxisRange(yAxis.makeDataItem({ value: cp.mean_before }));
                    beforeRange.get('grid').setAll({
                        stroke: am5.color('#64748b'),
                        strokeWidth: 1,
                        strokeOpacity: 0.6,
                        strokeDasharray: [5, 4],
                    });
                    beforeRange.get('label').setAll({
                        text: `avg before: ${Number(cp.mean_before).toFixed(2)}${this.units ? ` ${this.units}` : ''}`,
                        inside: true,
                        fontSize: 10,
                        fill: am5.color('#64748b'),
                        dx: 4,
                    });
                    this._yAxisRanges.push(beforeRange);
                }

                if (cp.mean_after != null) {
                    const afterRange = yAxis.createAxisRange(yAxis.makeDataItem({ value: cp.mean_after }));
                    afterRange.get('grid').setAll({
                        stroke: am5.color(MOVING_AVG_COLOR),
                        strokeWidth: 1,
                        strokeOpacity: 0.7,
                        strokeDasharray: [5, 4],
                    });
                    afterRange.get('label').setAll({
                        text: `avg after: ${Number(cp.mean_after).toFixed(2)}${this.units ? ` ${this.units}` : ''}`,
                        inside: true,
                        fontSize: 10,
                        fill: am5.color(MOVING_AVG_COLOR),
                        dx: 4,
                    });
                    this._yAxisRanges.push(afterRange);
                }
            });
        },

        clearFindingBands() {
            while (this._axisRanges.length) {
                const range = this._axisRanges.pop();
                if (range && !range.isDisposed()) {
                    range.dispose();
                }
            }
            while (this._yAxisRanges.length) {
                const range = this._yAxisRanges.pop();
                if (range && !range.isDisposed()) {
                    range.dispose();
                }
            }
        },

        updateFindingBands(finding = null) {
            if (!this._xAxis) {
                return;
            }

            this.clearFindingBands();

            const bands = finding ? [this.bandForFinding(finding)].filter(Boolean) : this.graphBands;
            bands.forEach((band) => {
                this.addFindingBand(this._xAxis, band);
            });

            if (finding) {
                this.addFindingMarkers(finding);
            }
        },

        // Vertical evidence markers for the focused finding: the regime transition for
        // stability findings, and each flag time for generic alert findings.
        addFindingMarkers(finding) {
            const supporting = finding?.supporting;
            if (!supporting || !this._xAxis) {
                return;
            }

            const inRange = (ms) => Number.isFinite(ms) && ms >= this.dataStartMs && ms <= this.dataEndMs;

            if (supporting.kind === 'regime_transition' && supporting.transition_at) {
                const atMs = new Date(supporting.transition_at).getTime();
                if (inRange(atMs)) {
                    const label = `${this.prettyRegime(supporting.from_regime)} → ${this.prettyRegime(supporting.to_regime)}`;
                    this.addFindingMarker(atMs, label, '#d97706');
                }
                return;
            }

            if (supporting.kind === 'alert_history' && Array.isArray(supporting.alert_times)) {
                const times = supporting.alert_times
                    .map((iso) => new Date(iso).getTime())
                    .filter(inRange);
                times.forEach((atMs, idx) => {
                    // Only label the most recent marker to avoid clutter.
                    const label = idx === times.length - 1 ? 'Flagged' : '';
                    this.addFindingMarker(atMs, label, '#dc2626');
                });
            }
        },

        addFindingMarker(atMs, label, color) {
            const range = this._xAxis.createAxisRange(this._xAxis.makeDataItem({ value: atMs }));
            range.get('grid').setAll({
                stroke: am5.color(color),
                strokeWidth: 2,
                strokeOpacity: 0.85,
                strokeDasharray: [4, 3],
                visible: true,
            });
            range.get('label').setAll({
                text: label || '',
                visible: Boolean(label),
                rotation: -90,
                centerY: am5.p100,
                centerX: am5.p0,
                paddingTop: 6,
                fontSize: 11,
                fontWeight: '600',
                fill: am5.color(color),
            });

            this._axisRanges.push(range);
        },

        prettyRegime(regime) {
            const normalized = String(regime || '').replaceAll('_', ' ').trim();
            return normalized ? normalized.replace(/\b\w/g, (c) => c.toUpperCase()) : '';
        },

        supportingText(finding) {
            const supporting = finding?.supporting;
            if (!supporting) {
                return '';
            }

            if (supporting.kind === 'regime_transition' && supporting.from_regime && supporting.to_regime) {
                const when = supporting.transition_at
                    ? ` on ${this.formatShortDate(new Date(supporting.transition_at).getTime())}`
                    : '';
                return `Steadiness moved from ${this.prettyRegime(supporting.from_regime)} to ${this.prettyRegime(supporting.to_regime)}${when} — click to see it marked on the graph.`;
            }

            if (supporting.kind === 'alert_history') {
                const source = supporting.source || 'automated analysis';
                const count = Number(supporting.alert_count || 0);
                if (count > 1 && supporting.first_alert_at && supporting.last_alert_at) {
                    const first = this.formatShortDate(new Date(supporting.first_alert_at).getTime());
                    const last = this.formatShortDate(new Date(supporting.last_alert_at).getTime());
                    return `Flagged ${count} times by ${source} between ${first} and ${last}.`;
                }
                const when = supporting.last_alert_at
                    ? ` on ${this.formatShortDate(new Date(supporting.last_alert_at).getTime())}`
                    : '';
                return `Flagged by ${source}${when}.`;
            }

            if (supporting.kind === 'volatility_level') {
                const swing = this.swingComparisonForFinding(finding);
                if (swing) {
                    const unitsBit = this.units ? ` ${this.units}` : '';
                    const pct = Math.round(swing.pct) || 0;
                    return `Recent swing about ±${this.formatSwing(swing.recent)}${unitsBit} vs ±${this.formatSwing(swing.baseline)} typical (${pct > 0 ? '+' : ''}${pct}%) — click to highlight the window.`;
                }
                if (supporting.level) {
                    return `Variability has been ${this.prettyRegime(supporting.level)} recently for this point.`;
                }
                return '';
            }

            return '';
        },

        // Compare swing (standard deviation) inside the finding window against the
        // baseline before it, using the readings already loaded for the master chart.
        swingComparisonForFinding(finding) {
            if (!this.points.length || !this.dataEndMs) {
                return null;
            }
            const windowDays = Number(finding?.supporting?.window_days || 21);
            const startMs = this.dataEndMs - (windowDays * 86400000);
            const baseline = this.points.filter((p) => p.date < startMs);
            const recent = this.points.filter((p) => p.date >= startMs);
            if (baseline.length < 30 || recent.length < 30) {
                return null;
            }
            const baselineSwing = estimateSwing(baseline);
            const recentSwing = estimateSwing(recent);
            if (!(baselineSwing > 0)) {
                return null;
            }
            return {
                baseline: baselineSwing,
                recent: recentSwing,
                pct: ((recentSwing - baselineSwing) / baselineSwing) * 100,
            };
        },

        formatSwing(value) {
            const n = Number(value);
            if (!Number.isFinite(n)) {
                return '—';
            }
            if (Math.abs(n) >= 100) {
                return n.toFixed(0);
            }
            if (Math.abs(n) >= 10) {
                return n.toFixed(1);
            }
            return n.toFixed(2);
        },

        bandForFinding(finding) {
            return this.graphBands.find((band) => {
                if (band.metricKey === finding.metric_key) {
                    return true;
                }

                if (band.grouped) {
                    const window = this.findingWindow(finding);
                    return window.startMs >= band.startMs && window.endMs <= band.endMs;
                }

                return false;
            }) || null;
        },

        addFindingBand(xAxis, band) {
            const meta = this.severityMeta(band.severity);
            const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: band.startMs }));
            range.set('endValue', band.endMs);
            range.get('axisFill').setAll({
                fill: am5.color(meta.bandFill),
                fillOpacity: meta.bandOpacity,
                visible: true,
            });
            range.get('grid').setAll({ visible: false });
            range.get('label').setAll({ visible: false });

            this._axisRanges.push(range);
        },

        focusFinding(finding, scrollList = true) {
            const key = finding.metric_key || '';

            if (this.isZoomedToFinding && this.activeFocusKey === key) {
                this.resetChartView(scrollList);
                return;
            }

            this.activeFocusKey = key;
            this.isZoomedToFinding = true;

            const findingRange = this.findingWindow(finding);
            const pad = 7 * 86400000;
            const zoomStart = Math.max(this.dataStartMs, findingRange.startMs - pad);
            const zoomEnd = Math.min(this.dataEndMs, findingRange.endMs + pad);

            this.suppressRangeUpdate = true;
            this.updateFindingBands(finding);

            if (this._xAxis) {
                this._xAxis.zoomToDates(new Date(zoomStart), new Date(zoomEnd));
            }

            this.applySeriesDataForRange(zoomStart, zoomEnd);

            window.setTimeout(() => {
                this.suppressRangeUpdate = false;
            }, 150);

            if (scrollList) {
                this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },

        resetChartView(scrollList = true) {
            this.activeFocusKey = '';
            this.isZoomedToFinding = false;
            this.suppressRangeUpdate = true;
            this.updateFindingBands(null);

            if (this._xAxis) {
                this._xAxis.zoomToDates(new Date(this.dataStartMs), new Date(this.dataEndMs));
            }

            this.applySeriesDataForRange(this.dataStartMs, this.dataEndMs);

            window.setTimeout(() => {
                this.suppressRangeUpdate = false;
            }, 150);

            if (scrollList) {
                this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },

        findingWindow(finding) {
            const endMs = this.dataEndMs;
            // detected_at is the analysis run time and can postdate the last reading;
            // clamp so windows never start after the data ends.
            const detected = Math.min(this.detectedMs(finding) ?? endMs, endMs);

            if (this.isDriftFinding(finding)) {
                return {
                    startMs: Math.max(this.dataStartMs, detected),
                    endMs,
                };
            }

            if (finding.type === 'volatility' || finding.metric_key === 'volatility') {
                return {
                    startMs: Math.max(this.dataStartMs, endMs - (21 * 86400000)),
                    endMs,
                };
            }

            if (finding.type === 'volatility_regime' || finding.metric_key === 'volatility_regime') {
                return {
                    startMs: Math.max(this.dataStartMs, detected),
                    endMs,
                };
            }

            return {
                startMs: Math.max(this.dataStartMs, detected - (14 * 86400000)),
                endMs,
            };
        },

        findingDateRangeLabel(finding) {
            const window = this.findingWindow(finding);
            return `${this.formatShortDate(window.startMs)} – ${this.formatShortDate(window.endMs)}`;
        },

        graphBandLabel(finding) {
            const label = String(finding.label || '');
            if (label.toLowerCase().includes('drifting upward')) {
                return 'Drifting upward';
            }
            if (label.toLowerCase().includes('drifting downward')) {
                return 'Drifting downward';
            }
            if (label.length > 28) {
                return `${label.slice(0, 25)}…`;
            }
            return label;
        },

        isDriftFinding(finding) {
            return DRIFT_TYPES.has(finding.type)
                || DRIFT_METRICS.has(finding.metric_key);
        },

        timelineBandStyle(band) {
            if (!this.dataStartMs || !this.dataEndMs) {
                return { display: 'none' };
            }
            const span = this.dataEndMs - this.dataStartMs;
            if (span <= 0) {
                return { display: 'none' };
            }
            const left = ((band.startMs - this.dataStartMs) / span) * 100;
            const width = ((band.endMs - band.startMs) / span) * 100;
            return {
                left: `${Math.max(0, left)}%`,
                width: `${Math.min(100 - left, Math.max(2, width))}%`,
            };
        },

        rowClasses(finding) {
            const focused = this.activeFocusKey && finding.metric_key === this.activeFocusKey;
            return focused
                ? 'bg-rose-50/80 ring-1 ring-inset ring-rose-200'
                : 'hover:bg-stone-50';
        },

        findingKey(finding, idx) {
            return `${finding.metric_key || finding.type || 'finding'}-${idx}`;
        },

        severityMeta(severity) {
            return SEVERITY_META[String(severity || 'low').toLowerCase()] || SEVERITY_META.low;
        },

        severityWeight(severity) {
            const weights = { high: 3, medium: 2, low: 1 };
            return weights[String(severity || 'low').toLowerCase()] || 0;
        },

        detectedMs(finding) {
            if (!finding?.detected_at) {
                return null;
            }
            const ts = new Date(finding.detected_at).getTime();
            return Number.isNaN(ts) ? null : ts;
        },

        relativeTime(iso) {
            if (!iso) {
                return 'recently';
            }
            const ts = new Date(iso).getTime();
            if (Number.isNaN(ts)) {
                return 'recently';
            }
            const diffDays = Math.round((Date.now() - ts) / 86400000);
            if (diffDays <= 0) {
                return 'today';
            }
            if (diffDays === 1) {
                return '1 day ago';
            }
            if (diffDays < 14) {
                return `${diffDays} days ago`;
            }
            if (diffDays < 60) {
                return `${Math.round(diffDays / 7)} weeks ago`;
            }
            return `${Math.round(diffDays / 30)} months ago`;
        },

        formatShortDate(value) {
            const date = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(date.getTime())) {
                return '—';
            }
            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        },

        formatTickDate(ms) {
            const date = new Date(ms);
            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        },

        disposeChart() {
            this._axisRanges = [];
            this._yAxisRanges = [];
            this._htmlLabels = [];
            this._chart = null;
            this._xAxis = null;
            this._yAxis = null;
            this._series = null;
            if (this.root) {
                this.root.dispose();
                this.root = null;
            }
        },
    },
};
</script>

<style scoped>
.analytics-results-findings-view {
    width: 100%;
}
</style>
